"use client";
import { useState, useEffect, useContext, createContext, useRef } from "react";
import { Card } from "../ui/card";
import { cn } from "../../lib/utils";
import { useSlideshow } from "../../lib/use-slideshow";
import { type BaseComponentProps } from "../../types/puck";
import { MapPin, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { describeReviewSources } from "./review-sources";

// Context for ServicePro config (will be provided by SSR wrapper or CSR app)
interface ServiceProConfig {
  apiUrl: string;
  siteId: string;
  slug: string;
  variant?: string;
  businessId?: string;
}

// Create a context that can be imported from external packages
export const ServiceProConfigContext = createContext<ServiceProConfig | undefined>(undefined);

// Global cache to prevent duplicate fetches across component instances
const reviewsCache = new Map<
  string,
  {
    reviews: any[];
    totalCount: number;
    sources: string[];
    reviewsPagePath: string | null;
    timestamp: number;
  }
>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Editor only. A live site renders no section at all rather than reviews nobody wrote.
const PLACEHOLDER_REVIEWS: ReviewItem[] = [
  {
    name: "Sarah Johnson",
    rating: 5,
    text: "Exceptional service! They arrived on time, diagnosed the problem quickly, and had it fixed within an hour. Professional and courteous.",
    date: "Jan 15, 2024",
  },
  {
    name: "Michael Chen",
    rating: 5,
    text: "Best decision we made was signing up for their maintenance plan. No more surprise breakdowns and energy bills have decreased significantly.",
    date: "Jan 10, 2024",
  },
  {
    name: "Jennifer Martinez",
    rating: 5,
    text: "Called them for an emergency repair on a Sunday. They came within 2 hours and had everything fixed fast. Fair pricing and excellent work.",
    date: "Jan 5, 2024",
  },
];

// One stable empty list: the fetch effect depends on the fallback's identity.
const NO_REVIEWS: ReviewItem[] = [];

// Render dates consistently regardless of source: a raw ISO date ("2026-05-22")
// from imported reviews becomes "May 22, 2026", while already-friendly strings
// ("Jan 15, 2024", "3 days ago") pass through untouched.
function formatReviewDate(date?: string): string | undefined {
  if (!date) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const parsed = new Date(`${date}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  }
  return date;
}

function ReviewerAvatar({ review }: { review: ReviewItem }) {
  if (review.profileImageUrl) {
    return (
      <img
        src={review.profileImageUrl}
        alt={review.name}
        referrerPolicy="no-referrer"
        className="jobsite:h-9 jobsite:w-9 jobsite:shrink-0 jobsite:rounded-full jobsite:object-cover"
      />
    );
  }

  // Reviews arrive from the API, so a missing name is a data question, not a
  // type one — and this used to throw, which takes the whole page down.
  const initials = (review.name ?? "")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="jobsite:flex jobsite:h-9 jobsite:w-9 jobsite:shrink-0 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:bg-muted jobsite:text-xs jobsite:font-semibold jobsite:text-muted-foreground">
      {initials}
    </div>
  );
}

// Review text with a "Read more" toggle that appears only when the text is
// actually clamped (grid and carousel cap at 6 lines; long reviews overflow).
function ReviewText({
  text,
  wrapperClassName,
  textClassName,
  onExpandChange,
}: {
  text: string;
  wrapperClassName?: string;
  textClassName: string;
  onExpandChange?: (expanded: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const el = textRef.current;
    if (!el) {
      return;
    }
    setIsClamped(el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  return (
    <div className={wrapperClassName}>
      <p ref={textRef} className={cn(textClassName, !expanded && "jobsite:line-clamp-6")}>
        &ldquo;{text}&rdquo;
      </p>
      {(isClamped || expanded) && (
        <button
          type="button"
          onClick={() => {
            const next = !expanded;
            setExpanded(next);
            onExpandChange?.(next);
          }}
          className="jobsite:mt-2 jobsite:text-sm jobsite:font-semibold jobsite:text-primary jobsite:hover:underline"
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
    </div>
  );
}

export interface ReviewItem {
  name: string;
  location?: string;
  rating: number;
  text: string;
  date?: string;
  profileImageUrl?: string;
  // "servicepro" = direct feedback from the review-request landing page;
  // every other value is a ReviewPlatform enum from the API.
  source?:
    | "google"
    | "yelp"
    | "facebook"
    | "thumbtack"
    | "nextdoor"
    | "bbb"
    | "angi"
    | "homeadvisor"
    | "other"
    | "servicepro";
}

export interface ReviewsProps extends BaseComponentProps {
  heading: string;
  subheading?: string;
  reviews: ReviewItem[];
  layout: "grid" | "carousel" | "wall" | "spotlight";
  showRatings: "yes" | "no";
  showDates: "yes" | "no";
  backgroundColor?: string;
  useRealReviews?: "yes" | "no"; // New prop to fetch real reviews
  reviewLimit?: number; // Number of reviews to display
  minRating?: number; // Minimum star rating to display (1-5)
  /** Passed by Puck config render to avoid iframe timing issues with window.__SERVICEPRO_CONFIG__ */
  puckSiteId?: string;
  puckApiUrl?: string;
  /** The business profile's Google review count, shown until the API's all-platform total loads. */
  totalReviewCount?: number;
  /**
   * The section is placed on every generated page and gated here, so a first
   * review shows up without regenerating. Outside the editor, no reviews means
   * no section; in the editor, sample reviews stand in with a note saying so.
   */
  isEditing?: boolean;
}

export const Reviews: React.FC<ReviewsProps> = ({
  heading,
  subheading,
  reviews: defaultReviewsProp,
  layout = "grid",
  showRatings = "yes",
  showDates = "yes",
  backgroundColor,
  useRealReviews = "yes",
  reviewLimit = 6,
  minRating = 4,
  puckSiteId,
  puckApiUrl,
  totalReviewCount: totalReviewCountProp,
  isEditing = false,
  className,
}) => {
  const defaultReviews = Array.isArray(defaultReviewsProp) && defaultReviewsProp.length > 0
    ? defaultReviewsProp
    : isEditing
      ? PLACEHOLDER_REVIEWS
      : NO_REVIEWS;
  const [reviews, setReviews] = useState<ReviewItem[]>(defaultReviews);
  const [_, setIsLoading] = useState(false);
  // The business profile's Google count paints first; the API's total, which adds
  // every other platform, replaces it once real reviews load.
  const [totalReviewCount, setTotalReviewCount] = useState<number>(
    totalReviewCountProp ?? defaultReviews.length,
  );
  const [reviewSources, setReviewSources] = useState<string[]>([]);
  // Path of the site's /reviews page (from the API) for the "View all" link
  const [reviewsPagePath, setReviewsPagePath] = useState<string | null>(null);
  // While a carousel review is expanded, pause auto-play so it doesn't slide away mid-read
  const [expandedCount, setExpandedCount] = useState(0);
  const fetchingRef = useRef(false);
  const hasFetchedRef = useRef(false);

  // Try to get config from context first (SSR), then fall back to window (CSR)
  const contextConfig = useContext(ServiceProConfigContext);
  const windowConfig =
    typeof window !== "undefined" ? (window as any).__SERVICEPRO_CONFIG__ : undefined;
  const config = contextConfig || windowConfig;

  // Puck props take priority — they're synchronously available in the editor
  // (window.__SERVICEPRO_CONFIG__ is set in a useEffect after paint, causing timing issues)
  const siteId = puckSiteId || config?.siteId;
  const apiUrl = puckApiUrl || config?.apiUrl;

  // Reset fetch guard when key inputs change so re-fetching happens
  const prevFetchKey = useRef<string | null>(null);
  const fetchKey = `${useRealReviews}-${siteId}-${reviewLimit}-${minRating}`;
  if (prevFetchKey.current !== null && prevFetchKey.current !== fetchKey) {
    hasFetchedRef.current = false;
  }
  prevFetchKey.current = fetchKey;

  // Fetch real reviews if enabled
  useEffect(() => {
    // Don't fetch if real reviews aren't enabled
    if (useRealReviews !== "yes") {
      setReviews(defaultReviews);
      return;
    }

    // Don't fetch if we don't have the required config
    if (!siteId || !apiUrl) {
      setReviews(defaultReviews);
      return;
    }

    // Don't fetch if already fetching or already fetched for this key
    if (fetchingRef.current || hasFetchedRef.current) {
      return;
    }

    const cacheKey = `${siteId}-${reviewLimit}-${minRating}`;

    // Check cache first
    const cached = reviewsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      const formattedReviews = cached.reviews.map((review: any) => ({
        name: review.profileName || "Anonymous",
        location: undefined,
        rating: Math.round(review.rating),
        text: review.reviewText || "",
        date:
          review.timeAgo ||
          (review.timestamp
            ? new Date(review.timestamp).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : undefined),
        profileImageUrl: review.profileImageUrl || undefined,
        source: review.source || "google",
      }));
      setReviews(formattedReviews);
      setTotalReviewCount(cached.totalCount);
      setReviewSources(cached.sources);
      setReviewsPagePath(cached.reviewsPagePath);
      hasFetchedRef.current = true;
      return;
    }

    const fetchReviews = async () => {
      fetchingRef.current = true;

      try {
        setIsLoading(true);

        const url = `${apiUrl}/api/sites/public/${siteId}/reviews?limit=${reviewLimit}&minRating=${minRating}`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error("Failed to fetch reviews");
        }

        const data = await response.json();

        setReviewsPagePath(data.reviewsPagePath ?? null);

        if (data.reviews && data.reviews.length > 0) {
          // Cache the raw response
          reviewsCache.set(cacheKey, {
            reviews: data.reviews,
            totalCount: data.totalCount || data.reviews.length,
            sources: data.sources ?? [],
            reviewsPagePath: data.reviewsPagePath ?? null,
            timestamp: Date.now(),
          });

          // Format reviews for display
          const formattedReviews = data.reviews.map((review: any) => ({
            name: review.profileName || "Anonymous",
            location: undefined,
            rating: Math.round(review.rating),
            text: review.reviewText || "",
            date:
              review.timeAgo ||
              (review.timestamp
                ? new Date(review.timestamp).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : undefined),
            profileImageUrl: review.profileImageUrl || undefined,
            source: review.source || "google",
          }));
          setReviews(formattedReviews);
          setTotalReviewCount(data.totalCount || formattedReviews.length);
          setReviewSources(data.sources ?? []);
        } else {
          // No real reviews found, use defaults
          setReviews(defaultReviews);
          setReviewSources([]);
          if (totalReviewCountProp === undefined) {
            setTotalReviewCount(defaultReviews.length);
          }
        }
      } catch {
        // Handled state, not a crash — fall back to the defaults rather than
        // logging into a visitor's console.
        setReviews(defaultReviews);
        setReviewSources([]);
      } finally {
        setIsLoading(false);
        fetchingRef.current = false;
        hasFetchedRef.current = true;
      }
    };

    fetchReviews();
  }, [useRealReviews, siteId, apiUrl, reviewLimit, minRating, defaultReviews]);

  // Auto-play carousel (paused while a review is expanded)
  const {
    index: currentIndex,
    next: nextSlide,
    prev: prevSlide,
    goTo: goToSlide,
  } = useSlideshow({
    count: reviews.length,
    intervalMs: 5000,
    paused: layout !== "carousel" || expandedCount > 0,
  });

  if (reviews.length === 0) {
    return null;
  }
  const showingPlaceholders = reviews === PLACEHOLDER_REVIEWS;

  const handleExpandChange = (expanded: boolean) => {
    setExpandedCount((count) => count + (expanded ? 1 : -1));
  };

  // "View all" links to the site's /reviews page when one exists, except when
  // the widget is already rendered on that page.
  const onReviewsPage =
    reviewsPagePath !== null &&
    typeof window !== "undefined" &&
    window.location.pathname.replace(/\/+$/, "") === reviewsPagePath;
  const viewAllPath = reviewsPagePath && !onReviewsPage ? reviewsPagePath : null;
  const surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const;
  const sourcesLine = useRealReviews === "yes" ? describeReviewSources(reviewSources) : null;

  const renderStars = (rating: number) => {
    return (
      <div className="jobsite:flex jobsite:gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={cn(
              "jobsite:w-5 jobsite:h-5",
              star <= rating ? "jobsite:fill-current" : "jobsite:fill-none",
            )}
          />
        ))}
      </div>
    );
  };

  return (
    <section
      className={cn(
        "jobsite:py-16 jobsite:md:py-24",
        backgroundColor ? "" : "jobsite:bg-muted",
        className,
      )}
      style={{
        backgroundColor: backgroundColor || undefined,
      }}
    >
      <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
        <div className="jobsite:text-center jobsite:mb-12 jobsite:max-w-3xl jobsite:mx-auto">
          <h2 className="jobsite:text-3xl jobsite:sm:text-4xl jobsite:md:text-5xl jobsite:mb-4 jobsite:font-heading jobsite:tracking-tight jobsite:text-balance jobsite:text-foreground">
            {heading}
          </h2>
          {subheading && (
            <p className="jobsite:text-lg jobsite:md:text-xl jobsite:font-body jobsite:text-pretty jobsite:text-muted-foreground">
              {subheading}
            </p>
          )}
          {showingPlaceholders && (
            <p className="jobsite:mt-4 jobsite:text-sm jobsite:font-body jobsite:text-muted-foreground">
              Sample reviews. This section appears on your site once you have a real review.
            </p>
          )}
        </div>

        {((showRatings === "yes" && reviews.length > 0) || sourcesLine) && (
          <div className="jobsite:flex jobsite:flex-col jobsite:items-center jobsite:gap-3 jobsite:mb-10">
            {showRatings === "yes" && reviews.length > 0 && (
              <div
                className="jobsite:inline-flex jobsite:items-center jobsite:gap-3 jobsite:px-6 jobsite:py-3 jobsite:bg-accent/10 jobsite:border jobsite:border-accent/20"
                style={surfaceRadiusStyle}
              >
                <div className="jobsite:text-accent jobsite:flex jobsite:gap-0.5">
                  {renderStars(5)}
                </div>
                <span className="jobsite:text-sm jobsite:font-semibold jobsite:text-foreground">
                  {/* The count is every review on every platform, not the five-star
                      ones — labelling it "Five Star" claimed a rating the number
                      never asserted. */}
                  {totalReviewCount}+ Reviews
                </span>
              </div>
            )}
            {sourcesLine && (
              <p className="jobsite:text-sm jobsite:text-center jobsite:font-body jobsite:text-muted-foreground">
                {sourcesLine}
              </p>
            )}
          </div>
        )}

        {layout === "spotlight" && reviews.length > 0 ? (
          // One quote, given room. The heading font carries it, so a serif theme reads
          // editorial; the aggregate badge above (showRatings) covers the numbers.
          <figure className="jobsite:mx-auto jobsite:max-w-3xl jobsite:text-center">
            <blockquote className="jobsite:font-heading jobsite:text-2xl jobsite:leading-snug jobsite:text-balance jobsite:text-foreground jobsite:sm:text-3xl">
              &ldquo;{reviews[0].text}&rdquo;
            </blockquote>
            <figcaption className="jobsite:mt-8 jobsite:flex jobsite:items-center jobsite:justify-center jobsite:gap-3">
              <ReviewerAvatar review={reviews[0]} />
              <span className="jobsite:font-heading jobsite:text-sm jobsite:text-foreground">
                {reviews[0].name}
              </span>
              {showDates === "yes" && reviews[0].date && (
                <span className="jobsite:text-xs jobsite:text-muted-foreground">
                  {formatReviewDate(reviews[0].date)}
                </span>
              )}
            </figcaption>
          </figure>
        ) : layout === "wall" ? (
          <div className="jobsite:columns-1 jobsite:sm:columns-2 jobsite:lg:columns-3 jobsite:gap-4">
            {reviews.map((review, idx) => (
              <Card
                key={idx}
                className="jobsite:mb-4 jobsite:break-inside-avoid jobsite:p-5 jobsite:relative jobsite:transition-colors jobsite:duration-200 jobsite:border jobsite:border-border/70 jobsite:hover:border-accent/40 jobsite:shadow-sm"
              >
                {showRatings === "yes" && (
                  <div className={cn("jobsite:flex jobsite:items-center jobsite:gap-1.5", review.text?.trim() ? "jobsite:mb-3" : "jobsite:mb-4 jobsite:justify-center jobsite:py-2")}>
                    <div className="jobsite:text-accent jobsite:flex jobsite:gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={cn(
                            review.text?.trim() ? "jobsite:w-3.5 jobsite:h-3.5" : "jobsite:w-6 jobsite:h-6",
                            star <= review.rating ? "jobsite:fill-current" : "jobsite:fill-none",
                          )}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {review.text?.trim() && (
                  <p className="jobsite:text-sm jobsite:leading-relaxed jobsite:mb-4 jobsite:font-body jobsite:text-card-foreground">
                    &ldquo;{review.text}&rdquo;
                  </p>
                )}

                <div className="jobsite:border-t jobsite:border-border/50 jobsite:pt-3">
                  <div className="jobsite:flex jobsite:items-center jobsite:justify-between">
                    <div className="jobsite:flex jobsite:items-center jobsite:gap-3">
                      <ReviewerAvatar review={review} />
                      <div className=" jobsite:text-sm jobsite:font-heading jobsite:text-card-foreground">
                        {review.name}
                      </div>
                    </div>
                    {showDates === "yes" && review.date && (
                      <div className="jobsite:text-xs jobsite:text-muted-foreground jobsite:shrink-0">
                        {formatReviewDate(review.date)}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : layout === "grid" ? (
          <div className="jobsite:grid jobsite:md:grid-cols-2 jobsite:lg:grid-cols-3 jobsite:gap-5">
            {reviews.map((review, idx) => (
              <Card
                key={idx}
                className="jobsite:flex jobsite:flex-col jobsite:p-7 jobsite:md:p-8 jobsite:relative jobsite:transition-all jobsite:duration-200 jobsite:border jobsite:border-border/70 jobsite:hover:-translate-y-0.5 jobsite:hover:border-accent/30 jobsite:hover:shadow-md jobsite:shadow-sm"
              >
                {showRatings === "yes" && (
                  <div className={cn("jobsite:text-accent", review.text?.trim() ? "jobsite:mb-4" : "jobsite:mb-6 jobsite:flex-1 jobsite:flex jobsite:items-center jobsite:justify-center")}>
                    <div className="jobsite:flex jobsite:gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={cn(
                            review.text?.trim() ? "jobsite:w-5 jobsite:h-5" : "jobsite:w-8 jobsite:h-8",
                            star <= review.rating ? "jobsite:fill-current" : "jobsite:fill-none",
                          )}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {review.text?.trim() && (
                  <ReviewText
                    text={review.text}
                    wrapperClassName="jobsite:flex-1 jobsite:mb-6"
                    textClassName="jobsite:text-base jobsite:md:text-lg jobsite:leading-relaxed jobsite:font-body jobsite:text-card-foreground"
                  />
                )}

                <div className="jobsite:border-t jobsite:border-border/50 jobsite:pt-5">
                  <div className="jobsite:flex jobsite:items-center jobsite:gap-3">
                    <ReviewerAvatar review={review} />
                    <div className="jobsite:min-w-0">
                      <div className=" jobsite:text-base jobsite:font-heading jobsite:text-card-foreground jobsite:truncate">
                        {review.name}
                      </div>
                      {(review.location || (showDates === "yes" && review.date)) && (
                        <div className="jobsite:mt-0.5 jobsite:text-sm jobsite:text-muted-foreground jobsite:truncate">
                          {[review.location, showDates === "yes" ? formatReviewDate(review.date) : undefined]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="jobsite:relative jobsite:max-w-3xl jobsite:mx-auto">
            {/*
              Vertical padding only. The clip is what keeps the neighbouring slides out of
              sight, but it was cutting the card's own drop shadow off flush at the top and
              bottom edges. Padding gives the shadow room inside the clip; horizontal padding
              would do the same for the neighbours, which is exactly what we don't want.
            */}
            <div className="jobsite:overflow-hidden jobsite:py-4">
              <div
                className="jobsite:flex jobsite:transition-transform jobsite:duration-500 jobsite:ease-in-out"
                style={{
                  transform: `translateX(-${currentIndex * 100}%)`,
                }}
              >
                {reviews.map((review, idx) => (
                  <div key={idx} className="jobsite:w-full jobsite:shrink-0 jobsite:px-2">
                    <Card className="jobsite:p-8 jobsite:md:p-10 jobsite:relative jobsite:h-full jobsite:flex jobsite:flex-col jobsite:border jobsite:border-border/70 jobsite:shadow-lg">
                      {showRatings === "yes" && (
                        <div className={cn("jobsite:text-accent", review.text?.trim() ? "jobsite:mb-4" : "jobsite:mb-8 jobsite:flex-1 jobsite:flex jobsite:items-center jobsite:justify-center jobsite:py-4")}>
                          <div className="jobsite:flex jobsite:gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={cn(
                                  review.text?.trim() ? "jobsite:w-5 jobsite:h-5" : "jobsite:w-10 jobsite:h-10",
                                  star <= review.rating ? "jobsite:fill-current" : "jobsite:fill-none",
                                )}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {review.text?.trim() && (
                        <ReviewText
                          text={review.text}
                          wrapperClassName="jobsite:flex-1 jobsite:mb-8"
                          textClassName="jobsite:text-lg jobsite:md:text-xl jobsite:leading-relaxed jobsite:font-body jobsite:text-card-foreground"
                          onExpandChange={handleExpandChange}
                        />
                      )}

                      <div className="jobsite:border-t jobsite:border-border/50 jobsite:pt-6">
                        <div className="jobsite:flex jobsite:items-center jobsite:justify-between">
                          <div className="jobsite:flex jobsite:items-center jobsite:gap-3">
                            <ReviewerAvatar review={review} />
                            <div>
                              <div className=" jobsite:text-base jobsite:sm:text-lg jobsite:font-heading jobsite:text-card-foreground">
                                {review.name}
                              </div>
                              {review.location && (
                                <div className="jobsite:mt-0.5 jobsite:text-sm jobsite:flex jobsite:items-center jobsite:gap-1.5 jobsite:text-muted-foreground">
                                  <MapPin className="jobsite:w-3.5 jobsite:h-3.5" />
                                  {review.location}
                                </div>
                              )}
                            </div>
                          </div>
                          {showDates === "yes" && review.date && (
                            <div className="jobsite:text-sm jobsite:text-muted-foreground jobsite:shrink-0">
                              {formatReviewDate(review.date)}
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  </div>
                ))}
              </div>
            </div>

            {reviews.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={prevSlide}
                  className="jobsite:absolute jobsite:left-0 jobsite:top-1/2 jobsite:-translate-y-1/2 jobsite:-translate-x-4 jobsite:z-10 jobsite:h-11 jobsite:w-11 jobsite:rounded-full jobsite:bg-background/90 jobsite:shadow-lg jobsite:hover:bg-accent jobsite:hover:text-accent-foreground jobsite:border-2"
                  aria-label="Previous review"
                >
                  <ChevronLeft className="jobsite:h-6 jobsite:w-6" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={nextSlide}
                  className="jobsite:absolute jobsite:right-0 jobsite:top-1/2 jobsite:-translate-y-1/2 jobsite:translate-x-4 jobsite:z-10 jobsite:h-11 jobsite:w-11 jobsite:rounded-full jobsite:bg-background/90 jobsite:shadow-lg jobsite:hover:bg-accent jobsite:hover:text-accent-foreground jobsite:border-2"
                  aria-label="Next review"
                >
                  <ChevronRight className="jobsite:h-6 jobsite:w-6" />
                </Button>
              </>
            )}

            {reviews.length > 1 && (
              <div className="jobsite:flex jobsite:justify-center jobsite:gap-2 jobsite:mt-8">
                {reviews.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => goToSlide(idx)}
                    className={cn(
                      "jobsite:w-3 jobsite:h-3 jobsite:rounded-full jobsite:transition-all jobsite:duration-300",
                      currentIndex === idx
                        ? "jobsite:bg-accent jobsite:w-8"
                        : "jobsite:bg-muted-foreground/30 jobsite:hover:bg-muted-foreground/50",
                    )}
                    aria-label={`Go to review ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {viewAllPath && (
          <div className="jobsite:mt-10 jobsite:text-center">
            <a
              href={viewAllPath}
              className="jobsite:inline-flex jobsite:items-center jobsite:gap-1.5 jobsite:font-semibold jobsite:text-primary jobsite:hover:underline"
            >
              View all {totalReviewCount > 0 ? `${totalReviewCount}+ ` : ""}reviews
              <ChevronRight className="jobsite:h-4 jobsite:w-4" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
};
